import { Component, OnInit, ViewChild } from '@angular/core';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AnalyticsEvent, AnalyticsPage, InternalAnalyticsService } from '../../core/internal-analytics.service';
import { CallFinishedNotificationChannel } from '../../core/agent/agent';
import { debounceTime, forkJoin } from 'rxjs';
import { AccountAction } from '../account.service';
import { User, UserService } from '../../core/user/user.service';
import { PageService } from '../../core/page.service';
import { BusinessService } from '../../core/business/business.service';
import { AgentService } from '../../core/agent/agent.service';
import { NotificationChannelType, NotificationChannel } from '../../core/business/business';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'rosie-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: false
})
export class NotificationsComponent implements OnInit {
  CallFinishedNotificationChannel = CallFinishedNotificationChannel;
  inProgress: AccountAction = AccountAction.None;
  errorMessage = '';
  selectedNotificationItem: { type: CallFinishedNotificationChannel, index: number, control: FormGroup } = { type: null, index: null, control: this.createNotification() };
  callNotifications: FormGroup;
  editedItemIndex: number;
  removeActionInProgress = false;
  isProduction = environment.name === 'production';

  @ViewChild('notificationModal') notificationModal: NgxSmartModalComponent;
  @ViewChild('pushNotificationModal') pushNotificationModal: NgxSmartModalComponent;

  constructor(
    private user: UserService,
    private business: BusinessService,
    private agent: AgentService,
    private analytics: InternalAnalyticsService,
    private page: PageService,
    private fb: FormBuilder,
    private modal: NgxSmartModalService
  ) {
    this.page.initializePage(`Rosie - Notifications`, AnalyticsPage.AccountNotifications);
  }

  get userData(): User {
    return this.user.userData;
  }

  get phoneNumbers(): FormArray {
    return this.callNotifications.get('notificationPhoneNumbers') as FormArray;
  }

  get emails(): FormArray {
    return this.callNotifications.get('notificationEmails') as FormArray;
  }

  get pushCallTypes(): FormArray {
    return this.callNotifications.get('pushCallTypes') as FormArray;
  }

  get isPhoneNumberAddInProgress(): boolean {
    return this.inProgress === AccountAction.AddAPhoneNumber;
  }

  get isEmailAddInProgress(): boolean {
    return this.inProgress === AccountAction.AddAnEmail;
  }

  get activeFormArray(): FormArray {
    if (this.selectedNotificationItem.type === this.CallFinishedNotificationChannel.Email) {
      return this.callNotifications.get('notificationEmails') as FormArray;
    } else {
      return this.callNotifications.get('notificationPhoneNumbers') as FormArray;
    }
  }

  get isEditingExistingItem(): boolean {
    return this.editedItemIndex != null;
  }

  ngOnInit(): void {
    this.createNotificationForm();
  }

  removePhoneNumber() {
    this.analytics.trackEvent(AnalyticsEvent.NotificationPhoneNumberRemoved);
    this.phoneNumbers.removeAt(this.editedItemIndex);
    this.handleModalClose();
  }

  removeEmail() {
    this.analytics.trackEvent(AnalyticsEvent.NotificationEmailRemoved);
    this.emails.removeAt(this.editedItemIndex);
    this.handleModalClose();
  }


  handleNotificationChangeSave() {
    const value = this.selectedNotificationItem.control.get('destination')?.value;
    const isDuplicate = [...this.phoneNumbers.value, ...this.emails.value]
      .some((item, index) => item.destination === value && index !== this.editedItemIndex);

    if (isDuplicate) {
      this.selectedNotificationItem.control.setErrors({ duplicate: true });
    }

    if (this.selectedNotificationItem.control.invalid) {
      this.selectedNotificationItem.control.markAsTouched();
      this.selectedNotificationItem.control.get('destination').markAsTouched();
      return;
    }

    if (this.isEditingExistingItem) {
      this.activeFormArray.at(this.selectedNotificationItem.index).patchValue(this.selectedNotificationItem.control.value);
    } else {
      this.activeFormArray.push(this.createNotification(this.selectedNotificationItem.control.value));
      this.handleAnalyticsAdd();
    }

    this.notificationModal.close();
  }

  handleNotification(index: number, type: any) {
    this.editedItemIndex = index;
    this.removeActionInProgress = false;
    this.selectedNotificationItem.type = type;
    this.selectedNotificationItem.index = index;

    let value;
    if (this.isEditingExistingItem) {
      value = this.activeFormArray.at(index)?.value;
    } else {
      value = {
        destination: '', type, configuration: { callTypes: [{ value: 'message', label: 'Message', isEnabled: true }, { value: 'appointment', label: 'Appointment', isEnabled: true }, { value: 'transferred', label: 'Call Transfers', isEnabled: true }, { value: 'spam', label: 'Spam', isEnabled: false }] }
      };
    }

    this.selectedNotificationItem.control = this.createNotification(value);
    this.selectedNotificationItem.control.markAsUntouched();
    this.selectedNotificationItem.control.get('destination').markAsUntouched();
    this.notificationModal.open();
  }

  handleItemRemoveAction() {
    this.removeActionInProgress = true;
  }

  handleItemRemoveCancel() {
    this.removeActionInProgress = false;
  }

  removeItem() {
    if (this.selectedNotificationItem?.type === CallFinishedNotificationChannel.SMS) {
      this.removePhoneNumber();
    } else {
      this.removeEmail();
    }
  }

  private handleAnalyticsAdd() {
    if (this.selectedNotificationItem?.type === CallFinishedNotificationChannel.SMS) {
      this.analytics.trackEvent(AnalyticsEvent.NotificationPhoneNumberAdded);
    } else {
      this.analytics.trackEvent(AnalyticsEvent.NotificationEmailAdded);
    }
  }

  private createNotificationForm() {
    const phoneNumbers = this.agent.primary?.business?.information?.additionalProperties?.notifications?.filter(n => n.type === NotificationChannelType.Sms) || [];
    const emails = this.agent.primary?.business?.information?.additionalProperties?.notifications?.filter(n => n.type === NotificationChannelType.Email) || [];
    const pushConfig = this.agent.primary?.business?.information?.additionalProperties?.notifications?.find(n => n.type === NotificationChannelType.Push);

    // Extract push call types if config exists
    const pushCallTypes = pushConfig?.configuration?.callTypes || {};

    this.callNotifications = this.fb.group({
      notificationPhoneNumbers: this.fb.array(phoneNumbers.map(n => this.createNotification(n))),
      notificationEmails: this.fb.array(emails.map(n => this.createNotification(n))),
      pushCallTypes: this.createNotificationChannels(
        Object.entries(pushCallTypes)
          .filter(([_, isEnabled]) => isEnabled)
          .map(([key]) => key)
      ),
      emailNotificationEnabled: this.agent.primary?.configuration?.callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.Email),
      smsNotificationEnabled: this.agent.primary?.configuration?.callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.SMS),
      pushNotificationEnabled: this.agent.primary?.configuration?.callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.Push),
    });

    this.callNotifications.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
      forkJoin([this.updateNotificationsContacts(), this.updateNotificationsSettings()]).subscribe(() => {
        this.agent.getAgent().subscribe();
      });
    });
  }

  private updateNotificationsSettings() {
    const value = this.callNotifications.value;
    const callFinishedNotificationChannel = [];
    if (value.emailNotificationEnabled) callFinishedNotificationChannel.push(CallFinishedNotificationChannel.Email);
    if (value.smsNotificationEnabled) callFinishedNotificationChannel.push(CallFinishedNotificationChannel.SMS);
    if (value.pushNotificationEnabled) callFinishedNotificationChannel.push(CallFinishedNotificationChannel.Push);
    return this.agent.updateAgentConfiguration({ configuration: { callFinishedNotificationChannel } }, true);
  }

  private updateNotificationsContacts() {
    const value = this.callNotifications.value;
    const phoneNumbers = value.notificationPhoneNumbers?.map((notification) => ({
      destination: notification.destination,
      type: NotificationChannelType.Sms,
      configuration: {
        callTypes: notification?.configuration?.callTypes.reduce((acc, type) => {
          acc[type.value] = type.isEnabled;
          return acc;
        }, {})
      }
    }));

    const emails = value.notificationEmails?.map((notification) => ({
      destination: notification.destination,
      type: NotificationChannelType.Email,
      configuration: {
        callTypes: notification?.configuration?.callTypes.reduce((acc, type) => {
          acc[type.value] = type.isEnabled;
          return acc;
        }, {})
      }
    }));

    // Push notifications - no destination, just call types
    const pushNotifications = [];
    if (value.pushNotificationEnabled && value.pushCallTypes) {
      const pushCallTypes = value.pushCallTypes.reduce((acc, type) => {
        acc[type.value] = type.isEnabled;
        return acc;
      }, {});

      // Only add push config if at least one call type is enabled
      if (Object.values(pushCallTypes).some(v => v)) {
        pushNotifications.push({
          type: NotificationChannelType.Push,
          configuration: {
            callTypes: pushCallTypes
          }
        });
      }
    }

    const businessInformationData: any = {
      additionalProperties: {
        notifications: [...phoneNumbers, ...emails, ...pushNotifications],
      }
    };
    return this.business.updateInformation(businessInformationData);
  }

  private handleModalClose() {
    this.editedItemIndex = null;
    this.removeActionInProgress = false;
    this.modal.closeAll();
  }

  private createNotification(notificationChannel?: NotificationChannel) {
    const validators = notificationChannel?.type === NotificationChannelType.Email ? [Validators.email, Validators.required] : [Validators.required];
    const callTypes = Array.isArray(notificationChannel?.configuration?.callTypes)
      ? notificationChannel?.configuration?.callTypes.filter((type: any) => type?.value ? type.isEnabled : true).map((type: any) => type?.value || type)
      : Object.entries(notificationChannel?.configuration?.callTypes || {}).filter(([_, isEnabled]) => isEnabled).map(([key]) => key);
    return this.fb.group({
      type: [notificationChannel?.type || '', Validators.required],
      destination: [notificationChannel?.destination || '', validators],
      configuration: this.fb.group({
        callTypes: this.createNotificationChannels(callTypes)
      })
    });
  }

  private createNotificationChannels(notificationChannels: string[] = []): FormArray {
    const supportedNotificationChannels = [{ value: 'message', label: 'Message' }, { value: 'appointment', label: 'Appointment' }, { value: 'transferred', label: 'Call Transfers' }, { value: 'spam', label: 'Spam' }, { value: 'hangup', label: 'Hang-ups' }];
    return this.fb.array(supportedNotificationChannels.map(channel => this.createNotificationChannel(channel, notificationChannels.includes(channel.value))));
  }

  createNotificationChannel(channel: { value: string, label: string }, isEnabled: boolean): FormGroup {
    return this.fb.group({ value: channel.value, label: channel.label, isEnabled: isEnabled });
  }

  openPushNotificationModal() {
    if (!this.callNotifications.get('pushNotificationEnabled')?.value) {
      return; // Only allow opening modal if push notifications are enabled
    }
    this.pushNotificationModal.open();
  }

  hasPushCallTypesEnabled(): boolean {
    return this.pushCallTypes?.controls?.some(control => control.value.isEnabled) ?? false;
  }
}
